import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from './ui/AppText';
import { captureException } from '../services/telemetry';

/**
 * ScreenErrorBoundary — boundary com granularidade de ROTA.
 *
 * Por que existe: o app tinha um único ErrorBoundary no topo. Qualquer tela que
 * estourasse na renderização derrubava os 65 screens e a navegação inteira.
 * Aqui o blast radius fica contido a uma tela: o resto da navegação (tab bar,
 * stack) permanece vivo.
 *
 * Recuperação que MUDA de estado (e não re-renderiza a mesma árvore quebrada):
 *   - "Tentar novamente": remonta a tela via bump de `resetKey` (estado novo,
 *     fetches refeitos).
 *   - "Voltar": navega para fora da tela quebrada (goBack se possível, senão
 *     reset para Home) — saída garantida mesmo se a tela re-estourar.
 *
 * Use via `withScreenBoundary(Component, name)` (abaixo), que injeta o
 * `navigation` da tela e o nome para contexto de telemetria.
 */
class ScreenErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, resetKey: 0 };
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        captureException(error, {
            scope: 'screen',
            screen: this.props.name ?? 'unknown',
            componentStack: errorInfo?.componentStack,
        });
    }

    handleRetry = () => {
        // Remonta a subárvore com chave nova: estado e queries da tela reiniciam.
        this.setState((s) => ({ hasError: false, resetKey: s.resetKey + 1 }));
    };

    handleGoBack = () => {
        const { navigation } = this.props;
        this.setState((s) => ({ hasError: false, resetKey: s.resetKey + 1 }));
        if (navigation?.canGoBack?.()) {
            navigation.goBack();
        } else {
            navigation?.reset?.({ index: 0, routes: [{ name: 'Home' }] });
        }
    };

    render() {
        if (this.state.hasError) {
            return (
                <View style={styles.container}>
                    <Text style={styles.icon}>⚠️</Text>
                    <Text style={styles.title}>Não foi possível abrir esta tela</Text>
                    <Text style={styles.message}>
                        Ocorreu um erro inesperado. Você pode tentar novamente ou voltar.
                    </Text>
                    <TouchableOpacity style={styles.button} onPress={this.handleRetry}>
                        <Text style={styles.buttonText}>Tentar novamente</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={styles.secondaryButton} onPress={this.handleGoBack}>
                        <Text style={styles.secondaryButtonText}>Voltar</Text>
                    </TouchableOpacity>
                </View>
            );
        }

        // `key` força a remontagem limpa da subárvore no retry.
        return <React.Fragment key={this.state.resetKey}>{this.props.children}</React.Fragment>;
    }
}

/**
 * HOC que envolve uma tela com seu próprio boundary, injetando o `navigation`
 * (para a ação "Voltar") e um `name` estável para a telemetria.
 */
export const withScreenBoundary = (ScreenComponent, name) => {
    const Wrapped = (props) => (
        <ScreenErrorBoundary navigation={props.navigation} name={name ?? props.route?.name}>
            <ScreenComponent {...props} />
        </ScreenErrorBoundary>
    );
    Wrapped.displayName = `withScreenBoundary(${name || ScreenComponent.displayName || ScreenComponent.name || 'Screen'})`;
    return Wrapped;
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#0B0D17',
        padding: 20,
    },
    icon: {
        fontSize: 56,
        marginBottom: 18,
    },
    title: {
        fontSize: 20,
        fontWeight: '700',
        color: '#E8EAED',
        marginBottom: 10,
        textAlign: 'center',
    },
    message: {
        fontSize: 14,
        color: '#A0A3B1',
        textAlign: 'center',
        marginBottom: 28,
        lineHeight: 20,
    },
    button: {
        backgroundColor: '#5B8FF9',
        paddingVertical: 14,
        paddingHorizontal: 30,
        borderRadius: 10,
        marginBottom: 12,
    },
    buttonText: {
        color: '#FFFFFF',
        fontSize: 16,
        fontWeight: '600',
    },
    secondaryButton: {
        paddingVertical: 10,
        paddingHorizontal: 24,
    },
    secondaryButtonText: {
        color: '#A0A3B1',
        fontSize: 14,
        fontWeight: '600',
    },
});

export default ScreenErrorBoundary;
